import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Package } from "lucide-react";
import api from "../lib/api";
import { useCart } from "../context/CartContext";

const MAX_ATTEMPTS = 6;

export default function Success() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const { clearCart } = useCart();
  const [status, setStatus] = useState("checking"); // checking | success | error | timeout
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    let active = true;

    const poll = async () => {
      if (!active) return;
      if (attempts >= MAX_ATTEMPTS) { setStatus("timeout"); return; }
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("success");
          if (!cleared.current) { clearCart(); cleared.current = true; }
          return;
        }
        if (data.status === "expired") { setStatus("error"); return; }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="success-page">
      {status === "checking" && (
        <div data-testid="payment-checking">
          <Loader2 className="w-16 h-16 mx-auto text-brand animate-spin mb-6" />
          <h1 className="text-3xl font-black mb-2">Confirming your payment...</h1>
          <p className="text-muted-foreground">Hang tight, this only takes a moment.</p>
        </div>
      )}
      {status === "success" && (
        <div data-testid="payment-success">
          <div className="w-20 h-20 mx-auto rounded-full border-2 border-ink bg-brand-green flex items-center justify-center mb-6 shadow-hard">
            <CheckCircle2 className="w-10 h-10" />
          </div>
          <h1 className="text-4xl font-black tracking-tighter mb-3">Order confirmed! 🎉</h1>
          <p className="text-muted-foreground mb-8">Thank you for your purchase. We're getting your order ready to ship.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/orders" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-bold bg-brand text-white border-2 border-ink shadow-hard-btn hover:translate-y-[3px] hover:shadow-none transition-all" data-testid="view-orders-button">
              <Package className="w-5 h-5" /> View My Orders
            </Link>
            <Link to="/shop" className="inline-flex items-center justify-center px-8 py-4 rounded-full font-bold bg-white border-2 border-ink shadow-hard-btn hover:translate-y-[3px] hover:shadow-none transition-all">
              Keep Shopping
            </Link>
          </div>
        </div>
      )}
      {(status === "error" || status === "timeout") && (
        <div data-testid="payment-error">
          <XCircle className="w-16 h-16 mx-auto text-brand-pink mb-6" />
          <h1 className="text-3xl font-black mb-2">{status === "timeout" ? "Still processing" : "Something went wrong"}</h1>
          <p className="text-muted-foreground mb-8">{status === "timeout" ? "Your payment is taking a while. Check My Orders shortly." : "We couldn't confirm your payment. Please try again."}</p>
          <Link to="/cart" className="inline-flex px-8 py-4 rounded-full font-bold bg-brand text-white border-2 border-ink shadow-hard-btn hover:translate-y-[3px] hover:shadow-none transition-all">
            Back to Cart
          </Link>
        </div>
      )}
    </div>
  );
}
